import React from 'react';
import { 
  Layer, 
  Line, 
  Stage, 
  Text 
} from 'react-konva';
import { Component } from '../../../dispatcher/modules';
import uuid from 'uuid-random';
import {
  BINARY_MULTIPLE_LAYERS_PROPS,
  BINARY_CELL_SEGMENT_DATA_STATE,
  BINARY_MULTIPLE_LAYER_DATA,
  BINARY_STRUCTURED_DATA
} from './Types';
import { Position } from '../../../dispatcher/Types';

class BinaryMultipleLayers extends Component<BINARY_MULTIPLE_LAYERS_PROPS, BINARY_CELL_SEGMENT_DATA_STATE> {
  layerRefs:any=[]
  stageRef:any = React.createRef();

  private tagOffset=6;
  private tagFontSize=8;
  private tagPickOffset=12;
  private tagHeight=12
  private textXOpen =2;
  private textXClosed = 8;
  private textXStandalone = 6;
  private color = '#1976d2';

  static defaultProps = {
    dataItem: {
      data: [
        {
          value: '<7>',
          id: '7',
          ctype: 'x-unknown',
          plTag: 'bpt',
          rid: '7',
          tagId: '7',
          pos: 'open'
        },
        {
          value: 'The application must be able to determine the maximum protection requirement for each column for each information asset according to the maximum principle and display it appropriately',
          plTag: 'text'
        },
        {
          value: '</7>',
          id: '107',
          plTag: 'ept',
          rid: '7',
          tagId: '107',
          pos: 'closed'
        }
      ],
      highLights: [
        {
          offset: 41,
          length: 4
        }
      ]
    }
  }

  constructor(props) {
    super(props);
    this.state = {
      images: {},
      imagesDimensions: []
    };
  }

  componentDidMount() {
    this.captureBinaryData(this.layerRefs);
  }

  componentDidUpdate() {
    const { dataItem } = this.props.dataItem;
    if (dataItem?.field === 'target') {
      this.dispatch('scrollToRowAfterLoadingCellSegment', {
        pos: Position.end,
        buffer: 500,
        stack: true
      }, {
        dataItem: { ...dataItem }
      });
    }
  }

  /**
   * Capture binary data for rendering images
   * @private
   * @param {Layer[]} layers
   */
  private captureBinaryData = (layers) => {
    const nonEmptyElements = layers.filter(Boolean);
    const imagesData = [];
    const imagesDimensions = [];

    for (let i = 0; i < nonEmptyElements.length; i++) {
      const imagesPoints = {};
      const { children: { 0: { attrs: { points } } } } = nonEmptyElements[i];
      const stage = nonEmptyElements[i].getCanvas();
      const rect = nonEmptyElements[i].getClientRect({
        relativeTo: this.stageRef.current
      });
      const dataURL = stage.toDataURL();
      const { width, height } = rect;
      points?.length == 10 ? Object.assign(imagesPoints, {
        standaloneTag: dataURL
      }) : (points?.length !== 10 && points?.[1] == 0) ? Object.assign(imagesPoints, {
        openedTag: dataURL
      }) : Object.assign(imagesPoints, {
        closedTag: dataURL
      });
      imagesData.push(imagesPoints);
      imagesDimensions.push({ 
        height: height - 2,
        width: width - 2
      });
    }
    this.setState({
      images: imagesData, 
      imagesDimensions: imagesDimensions 
    });
  };

  /**
   * Map binary data to React elements for rendering
   * @private
   */
  private mapData() {
    const { dataItem: { data, highLights } } = this.props;
    const { images, imagesDimensions } = this.state;
    let counter = -1;

    /**
     * Mapping given data structure to contain pos and length in text items
     * due to comparison with highlights position and offset
     */
    const textArray = data?.filter(item => item?.plTag === 'text');
    let textArrayCounter = 0;

    const structuredData = data?.map?.((item: BINARY_MULTIPLE_LAYER_DATA): BINARY_STRUCTURED_DATA => {
      if (item?.plTag === 'text') {
        textArrayCounter++;
        return ({
          ...item,
          position: textArray.slice(0, textArrayCounter - 1).reduce((accumulator, current) => accumulator + current.value.length, 0),
          length: item.value.length
        });
      } else return item;
    });

    /**
     * elements for text highlighting
     */
    const startTag = '<span class="tp-grid-highlight">';
    const endTag = '</span>';

    /**
     * Mapping structured data to (highlighted) text and tags
     */
    return structuredData?.map?.((item: BINARY_STRUCTURED_DATA, index:number) => {
      if (item?.plTag === 'text') {
        let text = item.value;
        const textBegin = item.position;
        const textLength = item.length;
        const textEnd = textBegin + textLength;
        if (highLights && (highLights.length > 0)) {
          let renderedText = '';
          let before = '';
          let middle = '';
          let after = '';
          const highlightStartInText = highLights.filter(highlight => (textBegin <= highlight.offset) && (highlight.offset < textEnd));
          const highlightEndInText = highLights.filter(highlight => (textBegin < (highlight.offset + highlight.length)) && ((highlight.offset + highlight.length) <= textEnd));
          const highlighOverText = highLights.filter(highlight => (highlight.offset <= textBegin) && (textEnd <= highlight.offset + highlight.length));
          const highlightOnlyStarts = highlightStartInText.filter(
            obj1 => !highlightEndInText.some(obj2 => obj1.offset === obj2.offset && obj1.length === obj2.length)
          );

          const highlightOnlyEnds = highlightEndInText.filter(
            obj2 => !highlightStartInText.some(obj1 => obj2.offset === obj1.offset && obj2.length === obj1.length)
          );

          const highlightsInsideText = highlightStartInText.filter(
            obj1 => highlightEndInText.some(obj2 => obj1.offset === obj2.offset && obj1.length === obj2.length)
          );

          const isHighlightEnds = highlightOnlyEnds.length > 0;
          const isHighlightInside = highlightsInsideText.length > 0;
          const isHighlightStart = highlightOnlyStarts.length > 0;
          const isHighlightOver = highlighOverText.length > 0;

          if (isHighlightEnds) {
            let highLightStart = highlightOnlyEnds[0].offset;
            let highLightStartLength = highlightOnlyEnds[0].length;
            let highLightEnd = highLightStart + highLightStartLength;

            if (!isHighlightInside && !isHighlightStart) {
              let beforeValue = text.substring(0, highLightEnd - textBegin);
              before = `${startTag}${beforeValue}${endTag}`;
              middle = text.substring(highLightEnd - textBegin, textEnd);
            }
            else if (isHighlightInside && !isHighlightStart) {
              let beforeValue = text.substring(0, highLightEnd - textBegin);
              before = `${startTag}${beforeValue}${endTag}`;
              let newtextBegin = textBegin + beforeValue.length;
              let newtextLength = textLength - beforeValue.length;

              highlightsInsideText.forEach((highlighItem) => {
                let unhighlighted = text.substring(newtextBegin - textBegin, highlighItem.offset - textBegin);
                let highlighted = text.substring(highlighItem.offset - textBegin, highlighItem.offset + highlighItem.length - textBegin);
                newtextBegin = newtextBegin + unhighlighted.length + highlighted.length;
                newtextLength = newtextLength + unhighlighted.length + highlighted.length;
                middle += `${unhighlighted}${startTag}${highlighted}${endTag}`;
              });

              after = text.substring(newtextBegin - textBegin, textLength);
            } else if (!isHighlightInside && isHighlightStart) {
              let highLightStart2 = highlightOnlyStarts[0].offset;
              let highLightStartLength2 = highlightOnlyStarts[0].length;
              let highLightEnd2 = highLightStart2 + highLightStartLength2;
              let beforeValue = text.substring(0, highLightEnd - textBegin);
              before = `${startTag}${beforeValue}${endTag}`;
              middle = text.substring(highLightEnd - textBegin, highLightStart2 - textBegin);
              let afterValue = text.substring(highLightStart2 - textBegin, highLightEnd2 - textBegin);
              after = `${startTag}${afterValue}${endTag}`;
            }
            else if (isHighlightInside && isHighlightStart) {
              let beforeValue = text.substring(0, highLightEnd - textBegin);
              before = `${startTag}${beforeValue}${endTag}`;
              let newtextBegin = textBegin + beforeValue.length;
              let newtextLength = textLength - beforeValue.length;

              let highLightStart2 = highlightOnlyStarts[0].offset;
              let highLightStartLength2 = highlightOnlyStarts[0].length;
              let highLightEnd2 = highLightStart2 + highLightStartLength2;

              highlightsInsideText.forEach((highlighsItem) => {
                let unhighlighted = text.substring(newtextBegin - textBegin, highlighsItem.offset - textBegin);
                let highlighted = text.substring(highlighsItem.offset - textBegin, highlighsItem.offset + highlighsItem.length - textBegin);
                newtextBegin = newtextBegin + unhighlighted.length + highlighted.length;
                newtextLength = newtextLength + unhighlighted.length + highlighted.length;
                middle += `${unhighlighted}${startTag}${highlighted}${endTag}`;
              });

              let afterValue = text.substring(highLightStart2 - textBegin, highLightEnd2 - textBegin);
              after = `${startTag}${afterValue}${endTag}`;
            }
          }
          else if (!isHighlightEnds) {
            if (!isHighlightInside && !isHighlightStart) {
              if (isHighlightOver) {
                let middleValue = text.substring(0, textLength);
                middle = `${startTag}${middleValue}${endTag}`;
              } else {
                middle = text;
              }
            }
            else if (isHighlightInside && !isHighlightStart) {
              let newtextBegin = textBegin;
              let newtextLength = textLength;

              highlightsInsideText.forEach((highlighssItem) => {
                let unhighlighted = text.substring(newtextBegin - textBegin, highlighssItem.offset - textBegin);
                let highlighted = text.substring(highlighssItem.offset - textBegin, highlighssItem.offset + highlighssItem.length - textBegin);

                newtextBegin = newtextBegin + unhighlighted.length + highlighted.length;
                newtextLength = newtextLength + unhighlighted.length + highlighted.length;
                middle += `${unhighlighted}${startTag}${highlighted}${endTag}`;
              });

              after = text.substring(newtextBegin - textBegin, textLength);
            } else if (!isHighlightInside && isHighlightStart) {
              let highLightStart2 = highlightOnlyStarts[0].offset;
              before = text.substring(0, highLightStart2 - textBegin);
              let afterValue = text.substring(highLightStart2 - textBegin, textEnd - textBegin);
              after = `${startTag}${afterValue}${endTag}`;
            }
            else if (isHighlightInside && isHighlightStart) {
              let highLightStart2 = highlightOnlyStarts[0].offset;
              let highLightStartLength2 = highlightOnlyStarts[0].length;
              let highLightEnd2 = highLightStart2 + highLightStartLength2;
              let newtextBegin = textBegin;
              let newtextLength = textLength;
              highlightsInsideText.forEach((highlighssssItem) => {
                let unhighlighted = text.substring(newtextBegin - textBegin, highlighssssItem.offset - textBegin);
                let highlighted = text.substring(highlighssssItem.offset - textBegin, highlighssssItem.offset + highlighssssItem.length - textBegin);
                newtextBegin = newtextBegin + unhighlighted.length + highlighted.length;
                newtextLength = newtextLength + unhighlighted.length + highlighted.length;
                before += `${unhighlighted}${startTag}${highlighted}${endTag}`;
              });
              middle = text.substring(newtextBegin - textBegin, highLightStart2 - textBegin);
              let afterValue = text.substring(highLightStart2 - textBegin, highLightEnd2 - textBegin);
              after = `${startTag}${afterValue}${endTag}`;
            }
          }
          renderedText = `${before}${middle}${after}`;
          return (
            <span
              key={index}
              className="tp-auto-height-cell-inner"
              dangerouslySetInnerHTML={{ __html: renderedText }}
            ></span>
          );
        } else {
          return (
            <span key={index} className="tp-auto-height-cell-inner">
              {text}
            </span>
          );
        }
      } else if (item?.pos === 'open' || item?.pos === 'closed' || item?.pos === 'close' || item?.pos === 'standalone') {
        const text = item?.value;
        const regex = /<([^>]+)>/;
        const match = text?.match(regex);
        const extractedValue = match[1];
        const valueText = extractedValue.replace('/', '');
        ++counter;
        return (
          <img
            className="tp-tag-img"
            key={index}
            height={imagesDimensions[counter]?.height}
            width={imagesDimensions[counter]?.width}
            src={item?.pos === 'open' ? images[counter]?.openedTag : item?.pos === 'standalone' ? images[counter]?.standaloneTag : images[counter]?.closedTag}
            alt={`Tag ${valueText} ${item?.pos}`}
          />
        );
      }
    });
  }

  /**
   * Render tags
   */

  render() {
    const stageElements = [];
    const { dataItem: { data } } = this.props;
    const numStages = data?.length;

    for (let i = 0; i < numStages; i++) {
      if (data[i].hasOwnProperty('pos')) {
        let tag = data[i].pos;
        if (tag === 'close') tag = 'closed';

        const text = data[i].value;
        const regex = /<([^>]+)>/;
        const match = text?.match(regex);
        const extractedValue = match[1];
        const extractedValueText = extractedValue.replace('/', '');

        const tagWidth = 6 * (extractedValueText.length + 3);
        const tagPoints = {
          open: [0, 0, this.tagOffset + tagWidth, 0, this.tagPickOffset + tagWidth, this.tagOffset, this.tagOffset + tagWidth, this.tagPickOffset, 0, this.tagPickOffset, 0, 0],
          closed: [0, this.tagOffset, this.tagOffset, 0, this.tagPickOffset + tagWidth, 0, this.tagPickOffset + tagWidth, this.tagPickOffset, this.tagOffset, this.tagPickOffset, 1, this.tagOffset],
          standalone: [0, 0, this.tagPickOffset + tagWidth, 0, this.tagPickOffset + tagWidth, this.tagPickOffset, 0, this.tagPickOffset, 0, 0]
        };

        stageElements.push(
          <Stage
            width={this.tagPickOffset + tagWidth}
            height={this.tagHeight}
            style={{ display: 'none' }}
            ref={this.stageRef}
            key={uuid()}
          >
            <Layer
              ref={ref => (this.layerRefs[i] = ref)}
              key={uuid()}
            >
              <Line
                points={tagPoints[tag]}
                stroke={this.color}
                strokeWidth={1}
                closed
                fill={this.color}
              />
              <Text
                width={tagWidth}
                align={'center'}
                x={tag == 'closed' ? this.textXClosed : tag == 'open' ? this.textXOpen : this.textXStandalone}
                fontSize={this.tagFontSize}
                fontStyle={'bold'}
                verticalAlign={'middle'}
                text={`Tag ${extractedValueText}`}
                y={(this.tagHeight - this.tagFontSize) / 2}
                fill={'white'}
              />
            </Layer>
          </Stage>
        );
      }
    }

    return (
      <>
        {this.mapData()}
        {stageElements}
      </>
    );
  }
}

export default BinaryMultipleLayers;
